import { ApolloError } from '@apollo/client';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import React, { FC } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import FormContainer, { PreviousPage } from '../Shared/FormContainer/FormContainer';
import { ErrorMessage } from '../Shared/Shared';
import { FormInputLabel, FormInputWrapper } from './styled';
import { FORM_INPUT_COMPONENT_TYPES } from './types';
interface FormInputProps {
  type: keyof typeof FORM_INPUT_COMPONENT_TYPES;
  formOptions: ReturnType<typeof useForm>;
  onSubmit: (data: Record<string, Array<string>>) => void;
  title?: string;
  loading?: boolean;
  error?: ApolloError | undefined;
  previousPage?: PreviousPage;
  accessibility?: boolean;
  nextButtonSiblings?: React.ReactNode;
  defaultValues?: string[] | number[];
  includeTip?: boolean;
}

const FormInput: FC<FormInputProps> = ({ type, formOptions, onSubmit, title, loading, error, previousPage, accessibility = true, nextButtonSiblings = false, defaultValues, includeTip = false }) => {
  const { t } = useTranslation();
  const { maxInputLength, minInputLength, length, placeholders, patterns, min, max } = FORM_INPUT_COMPONENT_TYPES[type];
  const tip = t(FORM_INPUT_COMPONENT_TYPES[type].tip);
  const defaultTitle = t(FORM_INPUT_COMPONENT_TYPES[type].title);
  const patternMessage = t(FORM_INPUT_COMPONENT_TYPES[type].patternMessage);
  const errorMessage = t(FORM_INPUT_COMPONENT_TYPES[type].errorMessage);
  const { register, handleSubmit, setFocus, formState, setValue, control } = formOptions;
  const defaultInput = defaultValues ? defaultValues.map(i => String(i)) : [...Array(length)].map(() => '');
  const inputs = useWatch({
    control,
    name: [...Array(length)].map((_, index) => `${type}.${index}`),
    defaultValue: defaultInput
  });

  const clearInputs = () => {
    [...Array(length)].forEach((_, index) => setValue(`${type}.${index}`, ''));
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedCode = e.clipboardData.getData('text').split('');
    if (pastedCode.length !== inputs.length) {
      return;
    }
    inputs.forEach((_, index) => setValue(`${type}.${index}`, pastedCode[index]));
    setFocus(`${type}.${length - 1}`);
  };

  const processInput = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const num = e.target.value;
    if (/[^0-9]/.test(num)) {
      return;
    }

    setValue(`${type}.${index}`, num);
    if (index !== length - 1 && (inputs[index]?.length || 0) + 1 === maxInputLength[index] && num.length !== 0) {
      setFocus(`${type}.${index + 1}`);
    }
  };

  const eraseInput = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === 'Backspace' && !inputs[index] && index !== 0) {
      setValue(`${type}.${index === length ? index - 1 : index}`, '');
      setFocus(`${type}.${index - 1}`);
    }
  };

  return (
    <FormInputWrapper>
      <FormContainer
        onSubmit={handleSubmit(onSubmit)}
        title={title || defaultTitle}
        accessibility={accessibility}
        nextButtonSiblings={nextButtonSiblings}
        loading={loading}
        previousPage={previousPage}
      >
        <FormInputLabel inputType={type} error={!!error || !!formState.errors?.[type]}>
          {inputs.map((num, index) => {
            return (
              <input
                {...register(`${type}.${index}`, {
                  required: {
                    value: true,
                    message: t('formInput.errorRequired', 'You must fill in all the fields')
                  },
                  maxLength: {
                    value: maxInputLength[index],
                    message: patternMessage
                  },
                  minLength: {
                    value: minInputLength[index],
                    message: patternMessage
                  },
                  pattern: {
                    value: patterns[index],
                    message: patternMessage
                  },
                  min: { value: min[index], message: patternMessage },
                  max: { value: max[index], message: patternMessage }
                })}
                key={index}
                type="text"
                inputMode="numeric"
                maxLength={maxInputLength[index]}
                minLength={minInputLength[index]}
                value={num}
                min={min[index]}
                max={max[index]}
                autoFocus={!inputs[0]?.length && index === 0}
                onPaste={handlePaste}
                onChange={e => processInput(e, index)}
                onKeyDown={e => eraseInput(e, index)}
                placeholder={placeholders[index]}
              />
            );
          })}
        </FormInputLabel>
        {!!tip && includeTip && <span style={{ fontSize: 14, marginBottom: 15, marginTop: 20, display: 'block' }}>{tip}</span>}
        {formState.errors?.[type] && <ErrorMessage>{formState?.errors?.[type]?.filter((err: Record<string, string>) => err)[0].message}</ErrorMessage>}
        {error && <ErrorMessage>{errorMessage}</ErrorMessage>}
      </FormContainer>
    </FormInputWrapper>
  );
};

export default FormInput;
